// External Imports
import { useState, useEffect, useContext } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase } from "../../util/format-text";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import Heading from "../UI/Heading";
import Modal from "../UI/Modal";
import PersonCard from "../UI/PersonCard";

/** ========================================================================
 * * Dispute Information Page
 * Renders a page, which displays all information about a dispute.
 * ========================================================================== */

export default function DisputeInfo({ open, setOpen, disputeId }) {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [dispute, setDispute] = useState({});
  const [loading, setLoading] = useState(true);

  // =================== FETCH RECORD ===================
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "dispute",
        id: disputeId,
        columns: [
          "customer { id firstName lastName phone photo }",
          "payment { description }",
        ],
      });
      setDispute(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== USE_EFFECT ===================
  useEffect(() => {
    if (disputeId) fetchRecord();
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal max-w-[800px]">
        <Heading title="Dispute Information" id={disputeId} isBackArrow={false}>
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>

        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Date Created"
            value={isoDateToString(
              dispute.dateCreated,
              "eee dd MMM, yyyy - hh:mm a"
            )}
            loading={loading}
          />
          <DetailPair
            label="Payment ID"
            onClick={(e) => navigate(`/payments/info?id=${dispute.paymentId}`, e)}
            value={dispute.paymentId}
            loading={loading}
          />
          <DetailPair
            label="Status"
            value={toTitleCase(dispute.status?.replaceAll("_", " "))}
            classes={`${
              dispute.status === "won"
                ? "text-green-600"
                : dispute.status === "lost"
                ? "text-red-600"
                : "text-yellow-600"
            } font-semibold text-lg`}
            loading={loading}
          />
          <div className="lg:col-span-3">
            <DetailPair
              label="Description"
              value={dispute.payment?.description}
              classes="font-semibold"
              loading={loading}
            />
          </div>
          <LinkPair
            label="Stripe ID"
            value={dispute.stripeId}
            link={
              dispute.stripeId
                ? `https://dashboard.stripe.com/disputes/${dispute.id}`
                : false
            }
            loading={loading}
          />
          <div className="lg:col-span-2">
            <DetailPair
              label="Dispute Reason"
              value={toTitleCase(dispute.reason?.replaceAll("_", " "))}
              loading={loading}
            />
          </div>

          <DetailPair
            label="Amount"
            classes={"font-bold text-lg"}
            value={`$${dispute.amount?.toFixed(2)}`}
            loading={loading}
          />
        </div>
        <div className="flex items-end justify-between text-sm">
          <div
            onClick={() =>
              window.open(
                `https://dashboard.stripe.com/disputes/${dispute.id}`,
                "_blank"
              )
            }
            className="btn-primary"
          >
            Manage on Stripe
          </div>
          <PersonCard
            link={`/customers/info?id=${dispute.customerId}`}
            label="CUSTOMER"
            name={`${dispute.customer?.firstName} ${dispute.customer?.lastName}`}
            secondary={dispute.customer?.phone}
            image={dispute.customer?.photo}
            loading={loading}
          />
        </div>
      </div>
    </Modal>
  );
}
